import React, { useEffect, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import SeoHead from './seo/SeoHead';
import JsonLd from './seo/JsonLd';
import { buildLandingJsonLd, LANDING_META_DESCRIPTION } from '../config/structuredData';
import { LANDING_TITLE } from './landing/landingContent';
import LandingHeader from './landing/LandingHeader';
import LandingFooter from './landing/LandingFooter';
import HeroSection from './landing/sections/HeroSection';
import ProductToursTeaserSection from './landing/sections/ProductToursTeaserSection';
import CandidatePillarsSection from './landing/sections/CandidatePillarsSection';
import ChoosePathSection from './landing/sections/ChoosePathSection';
import HowItWorksSection from './landing/sections/HowItWorksSection';
import AtsFeaturesSection from './landing/sections/AtsFeaturesSection';
import CandidateFeaturesSection from './landing/sections/CandidateFeaturesSection';
import InterviewerNetworkSection from './landing/sections/InterviewerNetworkSection';
import TestimonialsSection from './landing/sections/TestimonialsSection';
import FinalCtaSection from './landing/sections/FinalCtaSection';
import useCpraOptOut from '../hooks/useCpraOptOut';

const ExperienceJobMockAISection = lazy(() =>
  import('./landing/sections/ExperienceJobMockAISection')
);

const LandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cpraStatus, handleCpraOptOut } = useCpraOptOut();

  useEffect(() => {
    if (location.hash === '#platform-demo-videos') {
      navigate('/product-tours', { replace: true });
      return;
    }

    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [location.hash, navigate]);

  return (
    <>
      <SeoHead
        title={LANDING_TITLE}
        description={LANDING_META_DESCRIPTION}
        canonicalPath="/landing"
        ogType="website"
      />
      <JsonLd schema={buildLandingJsonLd()} />
      <Box>
        <LandingHeader />
        <HeroSection />
        <ProductToursTeaserSection />
        <CandidatePillarsSection />
        <Suspense fallback={null}>
          <ExperienceJobMockAISection />
        </Suspense>
        <ChoosePathSection />
        <HowItWorksSection />
        <AtsFeaturesSection />
        <CandidateFeaturesSection />
        <InterviewerNetworkSection />
        <TestimonialsSection />
        <FinalCtaSection />
        <LandingFooter cpraStatus={cpraStatus} onCpraOptOut={handleCpraOptOut} />
      </Box>
    </>
  );
};

export default LandingPage;
